import customAxios from "config";
import { renterError } from "@1-now/booking-core";
import { globalJson } from "global/global_json";
import { TrackedBooking } from "utils/trackBooking";

/**
 * Track my booking (1NOW-11157): an emailed one-time code, then all the renter's
 * bookings (upcoming, under way and over; not cancelled). Both calls are public, scoped to this site's company, and
 * answer every refusal with a 400 whose message is written for the renter
 * (bookings/track.py).
 */

/**
 * `resendAfter`: seconds until the next code may be asked for. The backend's wait
 * grows with each code (2, 5, 10, 20, 40, 60 minutes). `alreadySent`: a code sent
 * earlier is still good, so none went out now.
 */
export type RequestCodeResult =
  | { sent: true; resendAfter?: number; alreadySent?: boolean }
  | { error: string; resendAfter?: number };

const seconds = (v: unknown) => (typeof v === "number" && v > 0 ? Math.ceil(v) : undefined);

export type VerifyCodeResult =
  | { bookings: TrackedBooking[] }
  | { badCode: true }
  | { error: string };

/**
 * POST /api/bookings/track/request-code/. Answers 200 the same way whether or
 * not the email has a booking, so a 200 only ever means "if you have one, it
 * is on its way". A 400 is a message to show: a malformed address, or the
 * hourly limit.
 */
export const requestTrackCode = async (email: string): Promise<RequestCodeResult> => {
  try {
    const response = await customAxios.post<{ detail?: string; resend_after?: number }>("/api/bookings/track/request-code/", {
      company_id: globalJson.company_id,
      email,
    });
    return {
      sent: true,
      resendAfter: seconds(response.data?.resend_after),
      alreadySent: /already/i.test(response.data?.detail ?? ""),
    };
  } catch (error: any) {
    return { error: failure(error), resendAfter: seconds(error?.response?.data?.resend_after) };
  }
};

/**
 * POST /api/bookings/track/verify/. A wrong, expired, spent or locked code all
 * come back as one 400, which is "bad code" here and never anything finer.
 */
export const verifyTrackCode = async (email: string, code: string): Promise<VerifyCodeResult> => {
  try {
    const response = await customAxios.post<{ bookings?: TrackedBooking[] }>("/api/bookings/track/verify/", {
      company_id: globalJson.company_id,
      email,
      code,
    });
    return { bookings: Array.isArray(response.data?.bookings) ? response.data.bookings : [] };
  } catch (error: any) {
    if (error?.response?.status === 400) return { badCode: true };
    return { error: failure(error) };
  }
};

const failure = (error: any): string =>
  // "Booking not found" would read as a wrong code; a 404 here is the endpoint missing.
  error?.response?.status === 404
    ? "We can't look up bookings right now. Please try again later."
    : renterError(error, "booking");
